import { useEffect, useRef, useState } from "react";

import { DOMAINS, domainLabel } from "@/lib/domains";

/** One selectable row. `count` (optional) renders on the right — used by filter menus. */
export type DomainMultiSelectOption = { slug: string; label: string; count?: number };

type Props = {
  selected: string[];
  onToggle: (slug: string) => void;
  /** Rows to show. Defaults to the full domain list (no counts). */
  options?: DomainMultiSelectOption[];
  /** Cap the number of selections (unchecked rows disable once hit). Default: no cap. */
  max?: number;
  placeholder?: string;
  /** When provided and something is selected, shows a "Clear" row at the top. */
  onClear?: () => void;
  size?: "sm" | "md";
  align?: "left" | "right";
  /** Applied to the outer wrapper — set flex/margins here. */
  className?: string;
  /** Hide the inline selected-chips (render them elsewhere, e.g. their own row). */
  hideSelectedChips?: boolean;
};

// Mirror <Dropdown>'s size table so the two controls are pixel-consistent.
const SIZES = {
  sm: { trigger: "py-1 pl-2.5 pr-2 text-xs", option: "px-2.5 py-1.5 text-xs", icon: 12, width: "w-40" },
  md: { trigger: "py-2 pl-3 pr-2.5 text-sm", option: "px-3 py-1.5 text-sm", icon: 14, width: "w-full sm:w-48" },
};

/**
 * Multi-select variant of {@link Dropdown} — same themed trigger + menu chrome (built on the
 * `--bg-elev` / `--border` / `--shadow-card` vars and `text-text` / `text-primary` tokens, so it
 * adapts to light & dark automatically). The trigger is a static opener; the current selections
 * render as removable chips beside it (each with an ✕). Behaves like a checklist: clicking a menu
 * row toggles it and keeps the menu open. Closes on outside click.
 */
export default function DomainMultiSelect({
  selected,
  onToggle,
  options,
  max,
  placeholder = "Select domains…",
  onClear,
  size = "md",
  align = "left",
  className,
  hideSelectedChips = false,
}: Props) {
  const opts: DomainMultiSelectOption[] =
    options ?? DOMAINS.map((d) => ({ slug: d.slug, label: d.label }));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const sz = SIZES[size];
  const cap = max ?? Infinity;
  const atCap = selected.length >= cap;

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const labelFor = (slug: string) => opts.find((o) => o.slug === slug)?.label ?? domainLabel(slug);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
      {/* Trigger + menu (fixed width, so chips flow beside it) */}
      <div ref={ref} className={`relative shrink-0 ${sz.width}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] font-medium text-text/80 outline-none transition-colors hover:border-primary/40 focus:border-primary ${sz.trigger}`}
        >
          <span className="truncate text-text/60">{placeholder}</span>
          <svg
            className={`shrink-0 text-text/40 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
            width={sz.icon}
            height={sz.icon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <ul
            role="listbox"
            aria-multiselectable="true"
            className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full z-30 mt-1.5 max-h-60 min-w-full overflow-auto rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] p-1`}
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            {onClear && (
              <li>
                {/* Default row — selected when nothing specific is chosen; picking it shows all skills. */}
                <button
                  type="button"
                  role="option"
                  onClick={onClear}
                  aria-selected={selected.length === 0}
                  className={`flex w-full items-center justify-between gap-3 rounded-md text-left transition-colors ${sz.option} ${
                    selected.length === 0 ? "font-semibold text-primary" : "text-text/80"
                  } hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}
                >
                  <span className="truncate">All domains</span>
                  {selected.length === 0 && (
                    <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </li>
            )}
            {opts.map((o) => {
              const isSel = selected.includes(o.slug);
              const disabled = atCap && !isSel;
              return (
                <li key={o.slug}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    disabled={disabled}
                    onClick={() => onToggle(o.slug)}
                    className={`flex w-full items-center justify-between gap-3 rounded-md text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${sz.option} ${
                      isSel ? "font-semibold text-primary" : "text-text/80"
                    } hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}
                  >
                    <span className="truncate">{o.label}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      {o.count != null && <span className="text-text/40">{o.count}</span>}
                      {isSel && (
                        <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Selected domains as removable chips beside the trigger (unless rendered elsewhere) */}
      {!hideSelectedChips && selected.map((slug) => (
        <span
          key={slug}
          className="flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-elev)] py-1 pl-2.5 pr-1.5 text-xs font-medium text-text/80"
        >
          {labelFor(slug)}
          <button
            type="button"
            onClick={() => onToggle(slug)}
            aria-label={`Remove ${labelFor(slug)}`}
            className="flex text-text/40 transition-colors hover:text-text"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
    </div>
  );
}
